import React, { useState } from 'react';
import { Text, TooltipTrigger, SparkIcon, DropdownMenu, Spinner } from '@spark';
import ExtractedContentDisplay from '@/features/datasets/components/ExtractedContentDisplay';
import EmptyResponsesChoice from '@/features/datasets/components/datasetUpload/EmptyResponsesChoice';
import HasLabelsSwitch from '@/features/datasets/components/datasetUpload/HasLabelsSwitch';
import NoLabelsModeChoice from '@/features/datasets/components/datasetUpload/NoLabelsModeChoice';
import {
    NO_LABELS_MODE_DEFAULT,
} from '@/features/datasets/utils/hasLabelsHandling';
import { getGenerativeOutputNoun } from '@/shared/domain/taskTypes';
import '@/features/datasets/components/sparkSelectFullWidth.css';

/**
 * Per-objective form for generative outputs (Summarization / Content
 * Generation). The output lives in a single file column; we surface the
 * extracted preview to confirm the right column was picked.
 *
 * @param {Object} props
 * @param {Object} props.entry
 * @param {(patch: Object) => void} props.onPatch
 * @param {string} [props.taskType] - Drives the output noun ("summary" / "response").
 */
const SummarizationOutputForm = ({
    entry,
    onPatch,
    columns,
    columnsLoading,
    extractedSummary,
    summaryLoading,
    onOpenHelp,
    labelError,
    taskType,
}) => {
    const noun = getGenerativeOutputNoun(taskType);
    const [selectOpen, setSelectOpen] = useState(false);
    const hasLabels = entry?.hasLabels !== false;
    const noLabelsMode = entry?.noLabelsMode || NO_LABELS_MODE_DEFAULT;
    const normalizedOptions = (columns || []).map((opt) => {
        if (typeof opt === 'string') return { value: opt, label: opt };
        const v = opt?.value ?? '';
        return { value: v, label: opt?.label ?? String(v) };
    });
    const selectedOption = normalizedOptions.find((o) => o.value === entry.labelColumn);
    const items = normalizedOptions.map((o) => ({
        label: String(o.label),
        state: o.value === entry.labelColumn ? 'selected' : 'default',
        onClick: () => {
            onPatch({ labelColumn: o.value, summaryColumn: o.value });
            setSelectOpen(false);
        },
    }));
    return (
        <div>
            <HasLabelsSwitch
                taskType={taskType}
                checked={hasLabels}
                onChange={(checked) => {
                    if (checked) {
                        onPatch({
                            hasLabels: true,
                            noLabelsMode: NO_LABELS_MODE_DEFAULT,
                        });
                    } else {
                        onPatch({
                            hasLabels: false,
                            noLabelsMode: NO_LABELS_MODE_DEFAULT,
                            labelColumn: null,
                            summaryColumn: null,
                            extractedSummary: null,
                        });
                    }
                }}
            />

            {!hasLabels ? (
                <NoLabelsModeChoice
                    taskType={taskType}
                    value={noLabelsMode}
                    onChange={(mode) => onPatch({ noLabelsMode: mode })}
                />
            ) : (
                <>
                    <div style={{ marginBottom: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', gap: '8px' }}>
                            <Text variant="base-regular" style={{ fontWeight: 400, color: '#222', fontSize: '14px', margin: 0 }}>{`Select ${noun} field`}</Text>
                            <TooltipTrigger text={`Select the column that contains the ${noun} for the examples`}>
                                <span style={{ color: '#878787', cursor: 'help', display: 'inline-flex' }}>
                                    <SparkIcon slug="circle-help" size="small" />
                                </span>
                            </TooltipTrigger>
                            {columnsLoading ? <Spinner size="small" /> : null}
                        </div>
                        <DropdownMenu
                            selectType="single-select"
                            size="medium"
                            showLabel={false}
                            placeholder={columnsLoading ? 'Loading...' : `Select ${noun} column`}
                            text={selectedOption ? String(selectedOption.label) : undefined}
                            disabled={columnsLoading}
                            open={selectOpen}
                            onOpenChange={setSelectOpen}
                            items={items}
                            className="spark-select-full-width"
                        />
                        {labelError ? <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 6 }}>{labelError}</div> : null}
                    </div>
                    {entry.labelColumn && (
                        <div style={{ marginTop: 8 }}>
                            <ExtractedContentDisplay
                                title={`Extracted ${noun}`}
                                value={extractedSummary}
                                loading={summaryLoading}
                                onNeedHelpClick={onOpenHelp}
                            />
                            <EmptyResponsesChoice
                                isMultilabel={false}
                                taskType={taskType}
                                value={entry.emptyMultilabelHandling || 'unlabeled'}
                                onChange={(v) => onPatch({ emptyMultilabelHandling: v })}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default SummarizationOutputForm;
